import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { vitrinAdminService, organizationsService } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CONFIG = {
  claims: {
    title: 'Sahiplenme talepleri',
    api: 'claims',
    statusOptions: ['', 'new', 'in_review', 'approved', 'rejected'],
    statusLabels: { new: 'Yeni', in_review: 'İncelemede', approved: 'Onaylandı', rejected: 'Reddedildi' },
  },
  'edit-suggestions': {
    title: 'Düzenlenme önerileri',
    api: 'edit-suggestions',
    statusOptions: ['', 'new', 'applied', 'rejected'],
    statusLabels: { new: 'Yeni', applied: 'Uygulandı', rejected: 'Reddedildi' },
  },
  leads: {
    title: 'Gelen randevu talepleri',
    api: 'leads',
    statusOptions: ['', 'new', 'contacted', 'rejected'],
    statusLabels: { new: 'Yeni', contacted: 'İletişim kuruldu', rejected: 'Reddedildi' },
  },
  questions: {
    title: 'Sorular',
    api: 'questions',
    filterOptions: ['', 'pending', 'answered', 'published', 'private'],
    filterLabels: { pending: 'Cevapsız', answered: 'Cevaplı', published: 'Yayında', private: 'Gizli' },
  },
  complaints: {
    title: 'Şikayetler',
    api: 'complaints',
    statusOptions: ['', 'new', 'answered', 'archived'],
    statusLabels: { new: 'Yeni', answered: 'Yanıtlandı', archived: 'Arşivlendi' },
  },
  'customer-reviews': {
    title: 'Müşteri yorumları',
    api: 'customer-reviews',
  },
};

const ISSUE_LABELS = {
  closed: 'İşletme kapalı',
  moved: 'Taşındı',
  address_changed: 'Adres değişti',
  phone_wrong: 'Telefon hatalı',
  other: 'Diğer',
};

function fmt(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('tr-TR');
  } catch {
    return '—';
  }
}

function vitrinOrgUrl(slug) {
  if (!slug) return null;
  return `https://www.randu.app/isletme/${encodeURIComponent(slug)}`;
}

function StatusBadge({ value, labels }) {
  if (value == null || value === '') return <span className="text-muted">—</span>;
  const label = labels?.[value] || value;
  const cls =
    {
      new: 'warning',
      pending: 'warning',
      in_review: 'info',
      contacted: 'info',
      answered: 'success',
      applied: 'success',
      approved: 'success',
      published: 'success',
      rejected: 'danger',
      archived: 'secondary',
    }[value] || 'secondary';
  return <span className={`badge bg-${cls} text-wrap`}>{label}</span>;
}

function DetailField({ label, children, full }) {
  return (
    <div className={full ? 'col-12' : 'col-md-6'}>
      <div className="small text-muted text-uppercase fw-semibold mb-1" style={{ letterSpacing: '0.04em', fontSize: '0.7rem' }}>
        {label}
      </div>
      <div className="mb-3" style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
        {children}
      </div>
    </div>
  );
}

const VitrinAdminDynamicPage = () => {
  const { section } = useParams();
  const cfg = CONFIG[section];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [qDraft, setQDraft] = useState('');
  const [q, setQ] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgInput, setOrgInput] = useState('');
  const [orgResults, setOrgResults] = useState([]);
  const [orgOpen, setOrgOpen] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const orgWrapRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const [status, setStatus] = useState('');
  const [questionFilter, setQuestionFilter] = useState('');
  const [rating, setRating] = useState('');
  const [pendingReply, setPendingReply] = useState('');

  const [detail, setDetail] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [questionAnswerDraft, setQuestionAnswerDraft] = useState('');
  const [questionPublished, setQuestionPublished] = useState(false);
  const [questionPrivate, setQuestionPrivate] = useState(false);
  const [complaintAnswerDraft, setComplaintAnswerDraft] = useState('');
  const [complaintPublished, setComplaintPublished] = useState(false);
  const [complaintStatusDraft, setComplaintStatusDraft] = useState('new');
  const [adminNoteDraft, setAdminNoteDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkStatusTarget, setBulkStatusTarget] = useState('new');
  const headerCheckboxRef = useRef(null);

  const load = useCallback(async () => {
    if (!cfg) return;
    setLoading(true);
    const params = { page, limit: pageSize };
    if (q.trim()) params.q = q.trim();
    if (selectedOrg?.id) params.organization_id = String(selectedOrg.id);
    if (cfg.statusOptions && status) params.status = status;
    if (cfg.filterOptions && questionFilter) params.filter = questionFilter;
    if (section === 'customer-reviews') {
      if (rating) params.rating = rating;
      if (pendingReply !== '') params.pending_reply = pendingReply;
    }
    const res = await vitrinAdminService.list(cfg.api, params);
    if (res.success) {
      setRows(res.data || []);
      setPagination(res.pagination || { page: 1, limit: pageSize, total: 0, totalPages: 1 });
    } else {
      toast.error(res.error || 'Liste alınamadı');
      setRows([]);
    }
    setLoading(false);
  }, [cfg, page, pageSize, q, selectedOrg, status, questionFilter, rating, pendingReply, section]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [page, section, q, selectedOrg, status, questionFilter, pageSize]);

  useEffect(() => {
    setBulkStatusTarget('new');
  }, [section]);

  useEffect(() => {
    const el = headerCheckboxRef.current;
    if (!el) return;
    if (section !== 'claims' && section !== 'edit-suggestions') return;
    const idsOnPage = rows.map((r) => r.id);
    if (!idsOnPage.length) {
      el.indeterminate = false;
      return;
    }
    const n = idsOnPage.filter((id) => selectedRowIds.includes(id)).length;
    el.indeterminate = n > 0 && n < idsOnPage.length;
  }, [rows, selectedRowIds, section]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      setQ(qDraft);
    }, 420);
    return () => clearTimeout(searchDebounceRef.current);
  }, [qDraft]);

  useEffect(() => {
    const onDoc = (e) => {
      if (orgWrapRef.current && !orgWrapRef.current.contains(e.target)) setOrgOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!detail) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setDetail(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail]);

  useEffect(() => {
    if (!orgOpen || orgInput.trim().length < 2) {
      setOrgResults([]);
      return;
    }
    let cancelled = false;
    setOrgLoading(true);
    const t = setTimeout(async () => {
      const res = await organizationsService.getAllOrganizations({
        search: orgInput.trim(),
        page: 1,
        limit: 12,
        sortBy: 'name',
        sortOrder: 'ASC',
      });
      if (!cancelled) {
        setOrgLoading(false);
        if (res.success && Array.isArray(res.data)) setOrgResults(res.data);
        else setOrgResults([]);
      }
    }, 280);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [orgInput, orgOpen]);

  const tableHeaders = useMemo(() => {
    switch (section) {
      case 'claims':
        return ['ID', 'İşletme', 'Başvuran', 'İletişim', 'Durum', 'Tarih', ''];
      case 'edit-suggestions':
        return ['ID', 'İşletme', 'Konu', 'Özet', 'Durum', 'Tarih', ''];
      case 'leads':
        return ['ID', 'İşletme', 'Kişi', 'Not / tercih', 'Durum', 'Tarih', ''];
      case 'questions':
        return ['ID', 'İşletme', 'Soran', 'Özet', 'Durum', 'Tarih', ''];
      case 'complaints':
        return ['ID', 'İşletme', 'Konu', 'Özet', 'Durum', 'Tarih', ''];
      case 'customer-reviews':
        return ['ID', 'İşletme', 'Müşteri / puan', 'Yorum özeti', 'Yanıt', 'Tarih', ''];
      default:
        return ['ID', 'İşletme', '—', 'Özet', 'Durum', 'Tarih', ''];
    }
  }, [section]);

  if (!cfg) {
    return <Navigate to="/dashboard" replace />;
  }

  const bulkSection = section === 'claims' || section === 'edit-suggestions';

  const toggleRow = (id) => {
    setSelectedRowIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllPage = () => {
    const idsOnPage = rows.map((r) => r.id);
    if (!idsOnPage.length) return;
    const allOn = idsOnPage.every((id) => selectedRowIds.includes(id));
    if (allOn) {
      setSelectedRowIds((prev) => prev.filter((id) => !idsOnPage.includes(id)));
    } else {
      setSelectedRowIds((prev) => [...new Set([...prev, ...idsOnPage])]);
    }
  };

  const deleteRowById = async (id, opts = {}) => {
    if (!window.confirm('Bu kayıt kalıcı olarak silinecek. Emin misiniz?')) return;
    let res;
    if (section === 'claims') res = await vitrinAdminService.deleteClaim(id);
    else res = await vitrinAdminService.deleteEditSuggestion(id);
    if (res.success) {
      toast.success('Silindi');
      if (opts.closeDetail) setDetail(null);
      setSelectedRowIds((prev) => prev.filter((x) => x !== id));
      void load();
    } else {
      toast.error(res.error || res.message || 'Silinemedi');
    }
  };

  const bulkDeleteSelected = async () => {
    if (!selectedRowIds.length) {
      toast.warning('Önce en az bir kayıt seçin');
      return;
    }
    if (!window.confirm(`${selectedRowIds.length} kayıt kalıcı olarak silinecek. Onaylıyor musunuz?`)) return;
    let res;
    if (section === 'claims') res = await vitrinAdminService.bulkDeleteClaims(selectedRowIds);
    else res = await vitrinAdminService.bulkDeleteEditSuggestions(selectedRowIds);
    if (res.success) {
      toast.success(res.affected != null ? `${res.affected} kayıt silindi` : 'Silindi');
      setSelectedRowIds([]);
      void load();
    } else {
      toast.error(res.error || res.message || 'Toplu silinemedi');
    }
  };

  const applyBulkStatus = async () => {
    if (!selectedRowIds.length) {
      toast.warning('Önce en az bir kayıt seçin');
      return;
    }
    if (!window.confirm(`${selectedRowIds.length} kaydın durumu güncellenecek. Onaylıyor musunuz?`)) return;
    let res;
    if (section === 'claims') res = await vitrinAdminService.bulkStatusClaims(selectedRowIds, bulkStatusTarget);
    else res = await vitrinAdminService.bulkStatusEditSuggestions(selectedRowIds, bulkStatusTarget);
    if (res.success) {
      toast.success(res.affected != null ? `${res.affected} kayıt güncellendi` : 'Güncellendi');
      setSelectedRowIds([]);
      void load();
    } else {
      toast.error(res.error || res.message || 'Güncellenemedi');
    }
  };

  const openDetail = (row) => {
    setDetail(row);
    setReplyDraft(row.staff_reply || '');
    setQuestionAnswerDraft(row.answer || '');
    setQuestionPublished(!!row.is_published);
    setQuestionPrivate(!!row.is_private);
    setComplaintAnswerDraft(row.answer || '');
    setComplaintPublished(!!row.is_published);
    setComplaintStatusDraft(row.status || 'new');
    setAdminNoteDraft(row.admin_note || '');
    setStatusDraft(row.status || 'new');
  };

  const saveDetail = async () => {
    if (!detail) return;
    const oid = detail.organization_id;
    setSaving(true);
    let res = { success: false };
    try {
      if (section === 'customer-reviews') {
        res = await vitrinAdminService.patchCustomerReview(detail.id, {
          organization_id: oid,
          staff_reply: replyDraft,
        });
      } else if (section === 'questions') {
        res = await vitrinAdminService.patchQuestion(detail.id, {
          organization_id: oid,
          answer: questionAnswerDraft,
          is_published: questionPublished,
          is_private: questionPrivate,
        });
      } else if (section === 'complaints') {
        res = await vitrinAdminService.patchComplaint(detail.id, {
          organization_id: oid,
          answer: complaintAnswerDraft,
          is_published: complaintPublished,
          status: complaintStatusDraft,
        });
      } else if (section === 'edit-suggestions') {
        const body = { organization_id: oid, admin_note: adminNoteDraft };
        if (statusDraft) body.status = statusDraft;
        res = await vitrinAdminService.patchEditSuggestion(detail.id, body);
      } else if (section === 'claims') {
        const body = { organization_id: oid, admin_note: adminNoteDraft };
        if (statusDraft) body.status = statusDraft;
        res = await vitrinAdminService.patchClaim(detail.id, body);
      } else if (section === 'leads') {
        const body = { organization_id: oid, admin_note: adminNoteDraft };
        if (statusDraft) body.status = statusDraft;
        res = await vitrinAdminService.patchLead(detail.id, body);
      }
    } finally {
      setSaving(false);
    }
    if (res.success) {
      toast.success('Kaydedildi');
      setDetail(null);
      void load();
    } else {
      toast.error(res.error || res.message || 'Kaydedilemedi');
    }
  };

  const summaryCell = (row) => {
    if (row.summary) return String(row.summary).slice(0, 96);
    if (row.body) return String(row.body).slice(0, 96);
    if (row.comment) return String(row.comment).slice(0, 96);
    if (row.note) return String(row.note).slice(0, 96);
    if (row.subject) return String(row.subject).slice(0, 96);
    return '—';
  };

  const total = pagination.total || 0;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const renderRowCells = (row) => {
    const orgName = row.organization_name || `Org #${row.organization_id}`;
    const slug = row.organization_slug;
    const vitrin = vitrinOrgUrl(slug);
    const orgBlock = (
      <div>
        <div className="fw-semibold">{orgName}</div>
        {vitrin && (
          <a href={vitrin} target="_blank" rel="noopener noreferrer" className="small">
            Vitrinde aç →
          </a>
        )}
      </div>
    );

    switch (section) {
      case 'claims':
        return (
          <>
            <td className="align-middle text-muted">#{row.id}</td>
            <td className="align-middle">{orgBlock}</td>
            <td className="align-middle">
              <div className="fw-medium">{row.full_name}</div>
            </td>
            <td className="align-middle small">
              {row.phone && <div>{row.phone}</div>}
              {row.email && <div className="text-muted">{row.email}</div>}
            </td>
            <td className="align-middle">
              <StatusBadge value={row.status} labels={cfg.statusLabels} />
            </td>
            <td className="align-middle small text-muted">{fmt(row.created_at)}</td>
          </>
        );
      case 'edit-suggestions':
        return (
          <>
            <td className="align-middle text-muted">#{row.id}</td>
            <td className="align-middle">{orgBlock}</td>
            <td className="align-middle small">{ISSUE_LABELS[row.issue_type] || row.issue_type || '—'}</td>
            <td className="align-middle small">{summaryCell(row)}</td>
            <td className="align-middle">
              <StatusBadge value={row.status} labels={cfg.statusLabels} />
            </td>
            <td className="align-middle small text-muted">{fmt(row.created_at)}</td>
          </>
        );
      case 'leads':
        return (
          <>
            <td className="align-middle text-muted">#{row.id}</td>
            <td className="align-middle">{orgBlock}</td>
            <td className="align-middle">
              <div className="fw-medium">{row.full_name}</div>
              <div className="small text-muted">{row.phone}</div>
            </td>
            <td className="align-middle small">{summaryCell(row)}</td>
            <td className="align-middle">
              <StatusBadge value={row.status} labels={cfg.statusLabels} />
            </td>
            <td className="align-middle small text-muted">{fmt(row.created_at)}</td>
          </>
        );
      case 'questions':
        return (
          <>
            <td className="align-middle text-muted">#{row.id}</td>
            <td className="align-middle">{orgBlock}</td>
            <td className="align-middle">
              <div className="fw-medium">{row.full_name}</div>
              {row.phone_last4 && <div className="small text-muted">***{row.phone_last4}</div>}
            </td>
            <td className="align-middle small">{summaryCell(row)}</td>
            <td className="align-middle">
              {row.answer && String(row.answer).trim() ? (
                <span className="badge bg-success">Cevaplı</span>
              ) : (
                <span className="badge bg-warning text-dark">Bekliyor</span>
              )}
              {row.is_published ? <span className="badge bg-info ms-1">Yayında</span> : null}
            </td>
            <td className="align-middle small text-muted">{fmt(row.created_at)}</td>
          </>
        );
      case 'complaints':
        return (
          <>
            <td className="align-middle text-muted">#{row.id}</td>
            <td className="align-middle">{orgBlock}</td>
            <td className="align-middle small">{row.subject || '—'}</td>
            <td className="align-middle small">{summaryCell(row)}</td>
            <td className="align-middle">
              <StatusBadge value={row.status} labels={cfg.statusLabels} />
            </td>
            <td className="align-middle small text-muted">{fmt(row.created_at)}</td>
          </>
        );
      case 'customer-reviews':
        return (
          <>
            <td className="align-middle text-muted">#{row.id}</td>
            <td className="align-middle">{orgBlock}</td>
            <td className="align-middle">
              <div className="fw-medium">{row.customer_name || '—'}</div>
              <div className="small">{row.rating != null ? `${row.rating} ★` : '—'}</div>
            </td>
            <td className="align-middle small">{summaryCell(row)}</td>
            <td className="align-middle">
              {row.staff_reply && String(row.staff_reply).trim() ? (
                <span className="badge bg-success">Yanıtlı</span>
              ) : (
                <span className="badge bg-secondary">Bekliyor</span>
              )}
            </td>
            <td className="align-middle small text-muted">{fmt(row.created_at)}</td>
          </>
        );
      default:
        return null;
    }
  };

  const canSaveDetail =
    section === 'customer-reviews' ||
    section === 'questions' ||
    section === 'complaints' ||
    section === 'edit-suggestions' ||
    section === 'claims' ||
    section === 'leads';

  const detailTitle =
    section === 'claims'
      ? 'Sahiplenme talebi'
      : section === 'edit-suggestions'
        ? 'Düzenlenme önerisi'
        : section === 'leads'
          ? 'Randevu talebi'
          : section === 'questions'
            ? 'Soru'
            : section === 'complaints'
              ? 'Şikayet'
              : section === 'customer-reviews'
                ? 'Müşteri yorumu'
                : 'Kayıt';

  return (
    <MasterLayout>
      <ToastContainer />
      <Breadcrumb title={cfg.title} />

      <div
        className="card border-0 shadow-sm mb-3"
        style={{ borderRadius: '12px', overflow: 'hidden' }}
      >
        <div
          className="card-body p-4"
          style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #fff 50%)' }}
        >
          <div className="row g-3 align-items-end">
            <div className="col-lg-4 col-md-6" ref={orgWrapRef}>
              <label className="form-label fw-semibold mb-1">İşletme filtresi</label>
              <div className="position-relative">
                <input
                  className="form-control"
                  placeholder="İşletme adı yazın, listeden seçin…"
                  value={selectedOrg ? selectedOrg.name : orgInput}
                  onChange={(e) => {
                    setSelectedOrg(null);
                    setOrgInput(e.target.value);
                    setOrgOpen(true);
                  }}
                  onFocus={() => setOrgOpen(true)}
                />
                {selectedOrg && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link position-absolute end-0 top-50 translate-middle-y text-danger"
                    style={{ zIndex: 2 }}
                    onClick={() => {
                      setSelectedOrg(null);
                      setOrgInput('');
                      setPage(1);
                    }}
                  >
                    Temizle
                  </button>
                )}
                {orgOpen && !selectedOrg && orgInput.trim().length >= 2 && (
                  <div
                    className="list-group position-absolute w-100 shadow border rounded mt-1"
                    style={{ zIndex: 20, maxHeight: 260, overflow: 'auto' }}
                  >
                    {orgLoading && (
                      <div className="list-group-item small text-muted">Aranıyor…</div>
                    )}
                    {!orgLoading &&
                      orgResults.map((o) => (
                        <button
                          type="button"
                          key={o.id}
                          className="list-group-item list-group-item-action py-2 text-start"
                          onClick={() => {
                            setSelectedOrg({ id: o.id, name: o.name });
                            setOrgInput('');
                            setOrgOpen(false);
                            setPage(1);
                          }}
                        >
                          <span className="fw-medium">{o.name}</span>
                          <span className="text-muted small ms-2">#{o.id}</span>
                          {o.city && <div className="small text-muted">{o.city}</div>}
                        </button>
                      ))}
                    {!orgLoading && orgResults.length === 0 && (
                      <div className="list-group-item small text-muted">Sonuç yok</div>
                    )}
                  </div>
                )}
              </div>
              <div className="form-text">Organizasyon ID girmenize gerek yok; ad ile arayıp seçin.</div>
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label fw-semibold mb-1">Metin araması</label>
              <input
                className="form-control"
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
                placeholder="İsim, e-posta, telefon, not…"
              />
              <div className="form-text">Yazdıkça otomatik aranır.</div>
            </div>

            <div className="col-auto">
              <label className="form-label fw-semibold mb-1">Sayfa boyutu</label>
              <select
                className="form-select"
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
              >
                {[10, 20, 50].map((n) => (
                  <option key={n} value={n}>
                    {n} kayıt
                  </option>
                ))}
              </select>
            </div>

            {cfg.statusOptions && (
              <div className="col-auto">
                <label className="form-label fw-semibold mb-1">Durum</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => {
                    setPage(1);
                    setStatus(e.target.value);
                  }}
                >
                  {cfg.statusOptions.map((v) => (
                    <option key={v || 'all'} value={v}>
                      {v === '' ? 'Tümü' : cfg.statusLabels?.[v] || v}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {cfg.filterOptions && (
              <div className="col-auto">
                <label className="form-label fw-semibold mb-1">Filtre</label>
                <select
                  className="form-select"
                  value={questionFilter}
                  onChange={(e) => {
                    setPage(1);
                    setQuestionFilter(e.target.value);
                  }}
                >
                  {cfg.filterOptions.map((v) => (
                    <option key={v || 'all'} value={v}>
                      {v === '' ? 'Tümü' : cfg.filterLabels?.[v] || v}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {section === 'customer-reviews' && (
              <>
                <div className="col-auto">
                  <label className="form-label fw-semibold mb-1">Puan</label>
                  <select
                    className="form-select"
                    value={rating}
                    onChange={(e) => {
                      setPage(1);
                      setRating(e.target.value);
                    }}
                  >
                    <option value="">Tümü</option>
                    {[5, 4, 3, 2, 1].map((n) => (
                      <option key={n} value={String(n)}>
                        {n} yıldız
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-auto">
                  <label className="form-label fw-semibold mb-1">İşletme yanıtı</label>
                  <select
                    className="form-select"
                    value={pendingReply}
                    onChange={(e) => {
                      setPage(1);
                      setPendingReply(e.target.value);
                    }}
                  >
                    <option value="">Tümü</option>
                    <option value="1">Bekleyen</option>
                    <option value="0">Yanıtlı</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
        <div className="card-body p-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center px-4 py-3 border-bottom bg-white">
            <div className="small text-muted">
              {total > 0 ? (
                <>
                  <strong>{from}</strong> – <strong>{to}</strong> arası, toplam <strong>{total}</strong> kayıt
                </>
              ) : (
                'Kayıt bulunamadı'
              )}
            </div>
            {loading && (
              <div className="d-flex align-items-center gap-2 text-primary small">
                <span className="spinner-border spinner-border-sm" role="status" />
                Yükleniyor…
              </div>
            )}
          </div>
          {bulkSection && rows.length > 0 && (
            <div className="px-4 py-2 border-bottom bg-light d-flex flex-wrap align-items-center gap-2">
              <span className="small me-1">
                Seçili: <strong>{selectedRowIds.length}</strong>
              </span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', minWidth: 168 }}
                value={bulkStatusTarget}
                onChange={(e) => setBulkStatusTarget(e.target.value)}
              >
                {section === 'claims'
                  ? ['new', 'in_review', 'approved', 'rejected'].map((s) => (
                      <option key={s} value={s}>
                        {cfg.statusLabels[s]}
                      </option>
                    ))
                  : ['new', 'applied', 'rejected'].map((s) => (
                      <option key={s} value={s}>
                        {cfg.statusLabels[s]}
                      </option>
                    ))}
              </select>
              <button type="button" className="btn btn-sm btn-primary" onClick={() => void applyBulkStatus()}>
                Seçilenlerde durumu uygula
              </button>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => void bulkDeleteSelected()}>
                Seçilenleri sil
              </button>
            </div>
          )}
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.9rem' }}>
              <thead className="table-light">
                <tr>
                  {bulkSection && (
                    <th className="ps-3 py-3 border-0" style={{ width: 42 }}>
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        className="form-check-input"
                        title="Bu sayfadaki tümü"
                        checked={rows.length > 0 && rows.every((r) => selectedRowIds.includes(r.id))}
                        onChange={toggleSelectAllPage}
                      />
                    </th>
                  )}
                  {tableHeaders.map((h, hi) => (
                    <th key={`${h}-${hi}`} className="px-4 py-3 fw-semibold text-secondary border-0">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={tableHeaders.length + (bulkSection ? 1 : 0)}
                      className="text-center py-5 text-muted"
                    >
                      Filtrelere uygun kayıt yok. İşletme adı veya metin aramasını değiştirmeyi deneyin.
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={`${section}-${row.id}`}>
                    {bulkSection && (
                      <td className="align-middle ps-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedRowIds.includes(row.id)}
                          onChange={() => toggleRow(row.id)}
                        />
                      </td>
                    )}
                    {renderRowCells(row)}
                    <td className="px-4 text-end">
                      <div className="d-flex gap-1 justify-content-end flex-wrap">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary rounded-pill px-3"
                          onClick={() => openDetail(row)}
                        >
                          Görüntüle
                        </button>
                        {bulkSection && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger rounded-pill px-3"
                            onClick={() => void deleteRowById(row.id)}
                          >
                            Sil
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 py-3 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Önceki
              </button>
              <span className="small text-muted px-2">
                Sayfa {page} / {pagination.totalPages}
              </span>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </button>
            </div>
          )}
        </div>
      </div>

      {detail && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: 'rgba(15, 23, 42, 0.45)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
            <div className="modal-content border-0 shadow" style={{ borderRadius: '14px' }}>
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title fw-bold mb-0">
                    {detailTitle} <span className="text-muted fw-normal">#{detail.id}</span>
                  </h5>
                  {detail.organization_name && (
                    <div className="small text-muted mt-1">
                      {detail.organization_name}
                      {detail.organization_id != null && (
                        <span className="ms-2">· ID {detail.organization_id}</span>
                      )}
                      {vitrinOrgUrl(detail.organization_slug) && (
                        <a
                          className="ms-2"
                          href={vitrinOrgUrl(detail.organization_slug)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Vitrinde aç
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <button type="button" className="btn-close" aria-label="Kapat" onClick={() => setDetail(null)} />
              </div>
              <div className="modal-body pt-3">
                <div className="row g-0">
                  {section === 'edit-suggestions' && (
                    <>
                      <DetailField label="Başvuran">{detail.full_name}</DetailField>
                      <DetailField label="İletişim">
                        {detail.phone && <div>{detail.phone}</div>}
                        {detail.email && <div>{detail.email}</div>}
                        {!detail.phone && !detail.email && '—'}
                      </DetailField>
                      <DetailField label="Konu">{ISSUE_LABELS[detail.issue_type] || detail.issue_type}</DetailField>
                      <DetailField label="Durum">
                        <StatusBadge value={detail.status} labels={cfg.statusLabels} />
                      </DetailField>
                      <DetailField label="Öneri özeti" full>
                        <div className="p-3 bg-light rounded-3 border">{detail.summary || '—'}</div>
                      </DetailField>
                      <DetailField label="IP / istemci" full>
                        <span className="font-monospace small">{detail.ip || '—'}</span>
                        {detail.user_agent && (
                          <div className="small text-muted mt-1">{detail.user_agent}</div>
                        )}
                      </DetailField>
                    </>
                  )}

                  {section === 'claims' && (
                    <>
                      <DetailField label="Başvuran">{detail.full_name}</DetailField>
                      <DetailField label="İletişim">
                        <div>{detail.phone}</div>
                        {detail.email && <div>{detail.email}</div>}
                      </DetailField>
                      <DetailField label="Durum">
                        <StatusBadge value={detail.status} labels={cfg.statusLabels} />
                      </DetailField>
                      <DetailField label="Belgeler" full>
                        <div className="small">
                          {detail.tax_plate_url && (
                            <div>
                              Vergi levhası:{' '}
                              <a href={detail.tax_plate_url} target="_blank" rel="noopener noreferrer">
                                Görüntüle
                              </a>
                            </div>
                          )}
                          {detail.signature_circular_url && (
                            <div>
                              İmza sirküleri:{' '}
                              <a href={detail.signature_circular_url} target="_blank" rel="noopener noreferrer">
                                Görüntüle
                              </a>
                            </div>
                          )}
                          {!detail.tax_plate_url && !detail.signature_circular_url && '—'}
                        </div>
                      </DetailField>
                    </>
                  )}

                  {section === 'leads' && (
                    <>
                      <DetailField label="Kişi">{detail.full_name}</DetailField>
                      <DetailField label="İletişim">
                        <div>{detail.phone}</div>
                        {detail.email && <div>{detail.email}</div>}
                      </DetailField>
                      <DetailField label="Durum">
                        <StatusBadge value={detail.status} labels={cfg.statusLabels} />
                      </DetailField>
                      <DetailField label="Tercih edilen tarih">{detail.preferred_date || '—'}</DetailField>
                      <DetailField label="Tercih edilen saat">{detail.preferred_time || '—'}</DetailField>
                      <DetailField label="Not" full>
                        <div className="p-3 bg-light rounded-3 border">{detail.note || '—'}</div>
                      </DetailField>
                    </>
                  )}

                  {section === 'questions' && (
                    <>
                      <DetailField label="Soran">{detail.full_name}</DetailField>
                      <DetailField label="Telefon (son 4)">{detail.phone_last4 ? `***${detail.phone_last4}` : '—'}</DetailField>
                      <DetailField label="Soru" full>
                        <div className="p-3 bg-light rounded-3 border">{detail.body || '—'}</div>
                      </DetailField>
                      {detail.answer && (
                        <DetailField label="Mevcut cevap" full>
                          <div className="p-3 border rounded-3 bg-success bg-opacity-10">{detail.answer}</div>
                        </DetailField>
                      )}
                    </>
                  )}

                  {section === 'complaints' && (
                    <>
                      <DetailField label="Şikayetçi">{detail.full_name}</DetailField>
                      <DetailField label="Telefon (son 4)">{detail.phone_last4 ? `***${detail.phone_last4}` : '—'}</DetailField>
                      <DetailField label="Konu">{detail.subject || '—'}</DetailField>
                      <DetailField label="Durum">
                        <StatusBadge value={detail.status} labels={cfg.statusLabels} />
                      </DetailField>
                      <DetailField label="Şikayet metni" full>
                        <div className="p-3 bg-light rounded-3 border">{detail.body || '—'}</div>
                      </DetailField>
                    </>
                  )}

                  {section === 'customer-reviews' && (
                    <>
                      <DetailField label="Müşteri">{detail.customer_name || '—'}</DetailField>
                      <DetailField label="Puan">{detail.rating != null ? `${detail.rating} / 5` : '—'}</DetailField>
                      <DetailField label="Yorum" full>
                        <div className="p-3 bg-light rounded-3 border">{detail.comment || '—'}</div>
                      </DetailField>
                      {detail.staff_reply && (
                        <DetailField label="Mevcut işletme yanıtı" full>
                          <div className="p-3 border rounded-3">{detail.staff_reply}</div>
                        </DetailField>
                      )}
                    </>
                  )}

                  {(section === 'claims' ||
                    section === 'edit-suggestions' ||
                    section === 'leads') && (
                    <DetailField label="Oluşturulma" full>
                      {fmt(detail.created_at)}
                    </DetailField>
                  )}
                </div>

                {canSaveDetail && (
                  <hr className="my-4" />
                )}

                {section === 'customer-reviews' && (
                  <div>
                    <label className="form-label fw-semibold">İşletme yanıtı (vitrinde görünür)</label>
                    <textarea
                      className="form-control"
                      rows={5}
                      value={replyDraft}
                      onChange={(e) => setReplyDraft(e.target.value.slice(0, 4000))}
                      placeholder="Yanıtınızı yazın…"
                    />
                  </div>
                )}

                {section === 'questions' && (
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-semibold">Cevap</label>
                      <textarea
                        className="form-control"
                        rows={5}
                        value={questionAnswerDraft}
                        onChange={(e) => setQuestionAnswerDraft(e.target.value.slice(0, 8000))}
                      />
                    </div>
                    <div className="col-md-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={questionPublished}
                          onChange={(e) => setQuestionPublished(e.target.checked)}
                          id="qpub"
                        />
                        <label className="form-check-label" htmlFor="qpub">
                          Vitrinde yayınla
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={questionPrivate}
                          onChange={(e) => setQuestionPrivate(e.target.checked)}
                          id="qpriv"
                        />
                        <label className="form-check-label" htmlFor="qpriv">
                          Gizli (sadece işletme)
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {section === 'complaints' && (
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Kayıt durumu</label>
                      <select
                        className="form-select"
                        value={complaintStatusDraft}
                        onChange={(e) => setComplaintStatusDraft(e.target.value)}
                      >
                        <option value="new">Yeni</option>
                        <option value="answered">Yanıtlandı</option>
                        <option value="archived">Arşivlendi</option>
                      </select>
                    </div>
                    <div className="col-md-6 d-flex align-items-end">
                      <div className="form-check form-switch mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={complaintPublished}
                          onChange={(e) => setComplaintPublished(e.target.checked)}
                          id="cpub"
                        />
                        <label className="form-check-label" htmlFor="cpub">
                          Vitrinde yayınla
                        </label>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold">Yanıt metni</label>
                      <textarea
                        className="form-control"
                        rows={5}
                        value={complaintAnswerDraft}
                        onChange={(e) => setComplaintAnswerDraft(e.target.value.slice(0, 8000))}
                      />
                    </div>
                  </div>
                )}

                {(section === 'claims' || section === 'edit-suggestions' || section === 'leads') && (
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Durum</label>
                      <select
                        className="form-select"
                        value={statusDraft}
                        onChange={(e) => setStatusDraft(e.target.value)}
                      >
                        {section === 'claims' &&
                          ['new', 'in_review', 'approved', 'rejected'].map((s) => (
                            <option key={s} value={s}>
                              {cfg.statusLabels[s]}
                            </option>
                          ))}
                        {section === 'edit-suggestions' &&
                          ['new', 'applied', 'rejected'].map((s) => (
                            <option key={s} value={s}>
                              {cfg.statusLabels[s]}
                            </option>
                          ))}
                        {section === 'leads' &&
                          ['new', 'contacted', 'rejected'].map((s) => (
                            <option key={s} value={s}>
                              {cfg.statusLabels[s]}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold">Yönetici notu (iç kullanım)</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={adminNoteDraft}
                        onChange={(e) => setAdminNoteDraft(e.target.value.slice(0, 4000))}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 pt-0">
                {canSaveDetail && (
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    disabled={saving}
                    onClick={() => void saveDetail()}
                  >
                    {saving ? 'Kaydediliyor…' : 'Kaydet'}
                  </button>
                )}
                {bulkSection && detail && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => void deleteRowById(detail.id, { closeDetail: true })}
                  >
                    Sil
                  </button>
                )}
                <button type="button" className="btn btn-outline-secondary" onClick={() => setDetail(null)}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default VitrinAdminDynamicPage;
